import React, { useState, useRef, useEffect } from "react";
import { Drawer, Tabs, Tooltip } from "antd";
import { isEmpty, isString } from "lodash";
import { formatPhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import AiSummaryIcon from "../../icons/AISummaryIcon";
import AiSentimentIcon from "../../icons/AiSentimentIcon";
import { convertUtcToLocal } from "../../library/utils";
import { TitleCase } from "../../library/helpers";
import { DATETIME_FORMAT } from "../../constant/ReportConstants";
import { audioDownloader } from "../../utils/mediaDownloader";
import { useCallDetailById } from "./useCallByUser";
import CallAudioPlayer from "./CallAudioPlayer";
import TranscriptConversation from "./TranscriptConversation";
import ParticipantCard from "./ParticipantCard";
import CustomButton from "../commonComponents/button";
import OttoAiCallTopics from "./OttoAiCallTopics";
import LinkIcon from "../engagements/activityCenter/icons/LinkIcon";
import ExtensionCallIcon from "../../icons/ExtensionCallIcon";

const CALL_DIRECTION = {
  INBOUND: "INBOUND",
  OUTBOUND: "OUTBOUND",
};

const LoadingSkeleton = () => (
  <div className="grid grid-cols-12 gap-4">
    <div className="col-span-12">
      <div className="w-full h-[200px] bg-gray-100 animate-pulse" />
    </div>
  </div>
);

const ExpandCollapseButton = ({ isExpanded, count, onToggle }) => (
  <div className="flex items-center justify-end mt-2">
    <CustomButton
      type="link"
      className="!text-[#1677FF] !text-sm !font-semibold pr-0"
      btnText={isExpanded ? "Show Less" : `+${count} More`}
      handleClick={onToggle}
    />
  </div>
);

const formatDuration = seconds => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  const formatted = `${mins}m ${secs.toString().padStart(2, "0")}s`;
  return formatted;
};

const formatPhone = phoneNumber => {
  if (!isValidPhoneNumber(phoneNumber)) return phoneNumber;
  return formatPhoneNumber(phoneNumber);
};

const formatName = name => {
  const cleaned = (name || "").split(/ - | -|\/|\//);
  // check if cleaned array has at least one element
  if (cleaned.length === 0) return { pName: null, pRole: "" };
  // Return the first part as name and second part as role if exists
  if (cleaned.length === 1) return { pName: cleaned[0].trim() || null, pRole: "" };
  // If there are multiple parts, return the first as name and second as role
  if (cleaned.length > 2) {
    cleaned[1] = cleaned.slice(1).join(" ").trim(); // Join remaining parts as role
  }
  return { pName: cleaned[0].trim() || null, pRole: cleaned[1] || "" };
};

// Helper function to group participants by name
const groupParticipantsByName = participants => {
  const grouped = {};

  participants.forEach(p => {
    const { pName } = formatName(p?.calleeName || p?.name);
    const key = pName || (p?.phoneNumber ? formatPhone(p.phoneNumber) : "Unknown");

    if (!grouped[key]) {
      grouped[key] = {
        name: key,
        participants: [],
        phoneNumbers: new Map(), // Use Map to store unique phone numbers with extensions
        roles: new Set(),
      };
    }

    grouped[key].participants.push(p);

    // Handle phone numbers and extensions
    if (p?.phoneNumber) {
      const phoneNum = p.phoneNumber;
      const formattedPhone = formatPhone(phoneNum);
      const isExtension = !isValidPhoneNumber(phoneNum);

      if (isExtension) {
        // This is an extension - store it with any existing phone number
        const extensionNum = formattedPhone;
        grouped[key].phoneNumbers.set("extension", extensionNum);
      } else {
        // This is a phone number - store it
        grouped[key].phoneNumbers.set("phone", formattedPhone);
      }
    }

    const { pRole } = formatName(p?.calleeName || p?.name);
    if (pRole) {
      grouped[key].roles.add(pRole);
    }
  });

  return Object.values(grouped);
};

// Helper to format participant for ParticipantCard
const formatParticipantData = participant => {
  if (!participant) return null;

  const displayName =
    participant.lead?.firstName && participant.lead?.lastName
      ? `${participant.lead.firstName} ${participant.lead.lastName}`
      : participant.user?.firstName && participant.user?.lastName
      ? `${participant.user.firstName} ${participant.user.lastName}`
      : participant.calleeName || participant.name;

  const { pName, pRole } = formatName(displayName);
  const phoneNumbers = new Map();

  if (participant.phoneNumber) {
    const formattedPhone = formatPhone(participant.phoneNumber);
    const isExtension = !isValidPhoneNumber(participant.phoneNumber);
    phoneNumbers.set(isExtension ? "extension" : "phone", formattedPhone);
  }

  // Match old logic: if calleeName exists → receiver, otherwise → caller
  const isReceiver = !!participant.calleeName;

  return {
    name: pName || (participant?.phoneNumber ? formatPhone(participant.phoneNumber) : "Unknown"),
    phoneNumbers,
    roles: pRole ? new Set([pRole]) : new Set(),
    isReceiver,
    isCaller: !isReceiver,
  };
};

/**
 * Determines Lead and User participants from call records based on call direction
 *
 * @param {Array} participants - Array of call participant objects
 * @param {String} direction - Call direction: "INBOUND" or "OUTBOUND"
 *
 * @returns {Object} { lead, user, remaining } - Identified participants (grouped)
 *
 * LOGIC FLOW (Priority Order):
 * ============================
 *
 * 1. SORT: All participants by ID descending (newest first)
 * 2. GROUP: All participants by name using groupParticipantsByName
 *
 * 3. LEAD IDENTIFICATION (4 priority levels):
 *    Priority 1: Find group with lead data (p.lead.firstName/lastName)
 *    Priority 2: Based on call direction (name/calleeName):
 *       - INBOUND:  name field exists → Lead is caller
 *       - OUTBOUND: calleeName field exists → Lead is receiver
 *    Priority 3: PhoneNumber-only groups (no lead/user/name/calleeName):
 *       - INBOUND:  First phoneNumber-only → Lead (caller)
 *       - OUTBOUND: Second phoneNumber-only → Lead (receiver, since first = user/caller)
 *    Priority 4: First available group
 *
 * 4. USER IDENTIFICATION (4 priority levels):
 *    Priority 1: Find group with user data (p.user.firstName/lastName)
 *    Priority 2: Based on call direction (name/calleeName):
 *       - INBOUND:  calleeName field exists → User is receiver
 *       - OUTBOUND: name field exists → User is caller
 *    Priority 3: PhoneNumber-only groups (no lead/user/name/calleeName):
 *       - INBOUND:  Second phoneNumber-only → User (receiver, since first = lead/caller)
 *       - OUTBOUND: First phoneNumber-only → User (caller)
 *    Priority 4: First available group (excluding lead)
 *
 * 5. ASSIGN isCaller/isReceiver flags based on direction:
 *    - INBOUND:  lead = caller, user = receiver
 *    - OUTBOUND: lead = receiver, user = caller
 *
 * 6. REMAINING: Filter out lead and user groups
 */
const determineLeadAndUser = (participants, direction) => {
  try {
    // Safety check: ensure participants is an array
    if (!Array.isArray(participants) || participants.length === 0) {
      return { lead: null, user: null, remaining: [] };
    }

    // Step 1: Sort by ID descending
    const sortedParticipants = [...participants].sort(
      (a, b) => parseInt(b?.id || 0, 10) - parseInt(a?.id || 0, 10)
    );

    const isInbound = direction === CALL_DIRECTION.INBOUND;

    // Step 2: GROUP all participants by name
    const groupedParticipants = groupParticipantsByName(sortedParticipants);

    // Safety check: ensure grouping produced results
    if (!Array.isArray(groupedParticipants) || groupedParticipants.length === 0) {
      return { lead: null, user: null, remaining: [] };
    }

    // Helper: Check if group has name field (caller in original data)
    const hasName = group => {
      try {
        return group?.participants?.some(p => p?.name);
      } catch {
        return false;
      }
    };

    // Helper: Check if group has calleeName field (receiver in original data)
    const hasCalleeName = group => {
      try {
        return group?.participants?.some(p => p?.calleeName);
      } catch {
        return false;
      }
    };

    // Helper: Check if group is phoneNumber-only (no lead/user/name/calleeName)
    const isPhoneNumberOnly = group => {
      try {
        return group?.participants?.every(
          p => p?.phoneNumber && !p?.name && !p?.calleeName && !p?.user && !p?.lead
        );
      } catch {
        return false;
      }
    };

    // Get all phoneNumber-only groups for Priority 3
    const phoneOnlyGroups = groupedParticipants.filter(g => isPhoneNumberOnly(g)) || [];

    let lead;
    let user;

    // Step 3: Find LEAD
    // Priority 1: Has lead data
    lead = groupedParticipants.find(g =>
      g.participants.some(p => p.lead?.firstName && p.lead?.lastName)
    );

    // Priority 2: Based on direction (name/calleeName)
    if (isEmpty(lead)) {
      if (isInbound) {
        // INBOUND: Lead has 'name' field (caller)
        lead = groupedParticipants.find(g => hasName(g) && !isPhoneNumberOnly(g));
      } else {
        // OUTBOUND: Lead has 'calleeName' field (receiver)
        lead = groupedParticipants.find(g => hasCalleeName(g) && !isPhoneNumberOnly(g));
      }
    }

    // Priority 3: PhoneNumber-only groups
    if (isEmpty(lead)) {
      if (isInbound) {
        // INBOUND: First phoneNumber-only = Lead (caller)
        [lead] = phoneOnlyGroups;
      } else {
        // OUTBOUND: Second phoneNumber-only = Lead (receiver)
        // We'll determine this after finding user
      }
    }

    // Step 4: Find USER
    // Priority 1: Has user data
    user = groupedParticipants.find(g =>
      g.participants.some(p => p.user?.firstName && p.user?.lastName)
    );

    // Priority 2: Based on direction (name/calleeName)
    if (isEmpty(user)) {
      if (isInbound) {
        // INBOUND: User has 'calleeName' field (receiver), not same as lead
        user = groupedParticipants.find(
          g => hasCalleeName(g) && !isPhoneNumberOnly(g) && g.name !== lead?.name
        );
      } else {
        // OUTBOUND: User has 'name' field (caller), not same as lead
        user = groupedParticipants.find(
          g => hasName(g) && !isPhoneNumberOnly(g) && g.name !== lead?.name
        );
      }
    }

    // Priority 3: PhoneNumber-only groups
    if (isEmpty(user)) {
      if (isInbound) {
        // INBOUND: Second phoneNumber-only = User (receiver)
        user = phoneOnlyGroups.find(g => g.name !== lead?.name);
      } else {
        // OUTBOUND: First phoneNumber-only = User (caller)
        [user] = phoneOnlyGroups;

        // Now assign lead if it was pending (second phoneNumber-only)
        if (isEmpty(lead)) {
          lead = phoneOnlyGroups.find(g => g.name !== user?.name);
        }
      }
    }

    // Priority 4: First available
    if (isEmpty(lead) && groupedParticipants.length > 0) {
      [lead] = groupedParticipants;
    }

    if (isEmpty(user)) {
      user = groupedParticipants.find(g => g.name !== lead?.name);
    }

    // Step 5: Assign isCaller/isReceiver flags based on direction
    if (lead) {
      lead.isCaller = isInbound;
      lead.isReceiver = !isInbound;
    }

    if (user) {
      user.isCaller = !isInbound;
      user.isReceiver = isInbound;
    }

    // Step 6: Remaining groups - assign flags based on original name/calleeName fields
    const remaining = groupedParticipants.filter(
      g => g?.name !== lead?.name && g?.name !== user?.name
    );

    remaining.forEach(group => {
      if (!group) return;

      try {
        // Check if group has name or calleeName in original participants
        const hasNameField = hasName(group);
        const hasCalleeNameField = hasCalleeName(group);

        if (hasNameField && !hasCalleeNameField) {
          // Has name only → was a caller in original data
          group.isCaller = true;
          group.isReceiver = false;
        } else if (hasCalleeNameField && !hasNameField) {
          // Has calleeName only → was a receiver in original data
          group.isCaller = false;
          group.isReceiver = true;
        } else if (isPhoneNumberOnly(group)) {
          // PhoneNumber-only → default to receiver
          group.isCaller = false;
          group.isReceiver = true;
        } else {
          // Has both or neither → default to receiver
          group.isCaller = false;
          group.isReceiver = true;
        }
      } catch (error) {
        console.error("Error assigning flags to remaining group:", error);
        // Default to receiver on error
        group.isCaller = false;
        group.isReceiver = true;
      }
    });

    return { lead, user, remaining };
  } catch (error) {
    console.error("Error in determineLeadAndUser:", error);
    // Return safe defaults on any error
    return { lead: null, user: null, remaining: [] };
  }
};

const EmployeeCallDetailSummary = ({ visible, callDetails, onClose, title = "", callerName }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [participants, setParticipants] = useState([]); // Assuming participants is an array of objects
  const [transcriptUrl, setTranscriptUrl] = useState(""); // Assuming transcriptUrl is a string
  const [transcripts, setTranscripts] = useState([]); // Assuming transcripts is an array of objects
  const [activeTab, setActiveTab] = useState("summary");
  const [showAllParticipants, setShowAllParticipants] = useState(false);

  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  const duration = formatDuration(callDetails?.durationSeconds) || "0m 00s";
  const timestamp = callDetails?.startAt;
  const audioSrc =
    Array.isArray(callDetails?.callRecordings) &&
    callDetails.callRecordings.find(r => r && r.recordingUrl)?.recordingUrl;

  const { fetchCallDetailsbyId, data, loading } = useCallDetailById();

  // If callDetails is not available, fetch it using the call ID
  useEffect(() => {
    if (callDetails?.id && callDetails?.callSid) {
      // Fetch call details by ID
      fetchCallDetailsbyId({
        variables: { callId: callDetails?.id, callSid: callDetails?.callSid },
      }).then(resp => {
        if (resp && resp.data && resp.data.getCall) {
          // Update callDetails with fetched data
          if (resp.data.getCall.callParticipants) {
            setParticipants(resp.data.getCall.callParticipants || []);
          }
          if ((resp.data.getCall.callRecordings || []).length > 0) {
            // get transcriptUrl url object from callRecordings first one and not null
            // Assuming transcripts are stored in callRecordings
            // Find the first callRecording where callParticipant.userId is not null
            const recordings = resp.data.getCall.callRecordings || [];
            const userRecording = recordings.find(r => r?.callParticipant?.userId);
            const UnknownRecording = recordings.find(
              r => !r?.callParticipant?.userId && !r?.callParticipant?.leadId
            );
            setTranscriptUrl(
              userRecording?.transcriptUrl ||
                UnknownRecording?.transcriptUrl ||
                recordings[0]?.transcriptUrl ||
                ""
            );
          }
        }
      });
    }
  }, [callDetails]);

  // if transcriptUrl is set, fetch transcripts
  useEffect(() => {
    if (transcriptUrl && transcriptUrl.trim() !== "") {
      // Fetch transcripts using the transcriptUrl
      const fetchTranscripts = async url => {
        try {
          // Use your backend as a proxy to avoid CORS issues
          const response = await fetch(url, {
            headers: {
              "Content-Type": "application/json",
            },
          });
          if (!response.ok) throw new Error("Failed to fetch transcripts");
          return await response.json();
        } catch (error) {
          console.error("Error fetching transcripts:", error);
          return [];
        }
      };

      fetchTranscripts(transcriptUrl).then(fetchedTranscripts => {
        // Assuming fetchedTranscripts is an array of objects
        if (!Array.isArray(fetchedTranscripts)) {
          setTranscripts([]);
        } else {
          setTranscripts(fetchedTranscripts);
        }
      });
    }
  }, [transcriptUrl]);

  /**
   * Toggles the mute state of the audio player
   */
  const handleToggleMute = () => {
    const audio = audioRef.current;

    if (audio) {
      // Direct control of <audio> element
      audio.muted = !audio.muted;
      setIsMuted(audio.muted);
    } else {
      // For external APIs (WebRTC, Twilio), use this toggle
      setIsMuted(prev => !prev);
      // Example: externalStream?.getAudioTracks()[0].enabled = !isMuted;
    }
  };

  const formatTime = timeInSeconds => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  // Parse duration string in "mm:ss" format to seconds
  const parseDuration = durationStr => {
    if (!durationStr) return 0;
    const [minutes, seconds] = durationStr.split(":").map(Number);
    return minutes * 60 + seconds;
  };

  const totalDurationInSeconds = parseDuration(duration);

  /**
   * Handles the download of the audio file
   */
  const handleDownloadAudio = () => {
    try {
      setIsDownloading(true);
      const fileName = title ? `call-with-${title}` : "call-recording";
      audioDownloader(audioSrc, fileName, timestamp, () => {
        setIsDownloading(false);
      });
    } catch (error) {
      setIsDownloading(false);
    }
  };

  /**
   * Handles play/pause toggle
   */
  const handlePlayPause = () => {
    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Update audio progress
  const startTimer = () => {
    clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      if (audioRef.current) {
        const current = audioRef.current.currentTime;
        setCurrentTime(current);
        setProgress((current / totalDurationInSeconds) * 100);

        if (current >= totalDurationInSeconds) {
          setIsPlaying(false);
          clearInterval(intervalRef.current);
        }
      }
    }, 1000);
  };

  // Handle seek on slider change
  const handleSliderChange = value => {
    // Prevent division by zero or NaN
    if (
      !audioRef.current ||
      !Number.isFinite(totalDurationInSeconds) ||
      totalDurationInSeconds <= 0
    ) {
      setProgress(0);
      setCurrentTime(0);
      return;
    }
    const seekTime = (value / 100) * totalDurationInSeconds;
    setProgress(value);
    setCurrentTime(seekTime);
    if (audioRef.current && Number.isFinite(seekTime)) {
      audioRef.current.currentTime = seekTime;
    }
  };

  const moreMenuItems = [
    {
      key: "1",
      label: "Download",
      onClick: handleDownloadAudio,
    },
    // { key: "2", label: "Share" },
  ];

  useEffect(() => {
    if (isPlaying) {
      startTimer();
    } else {
      clearInterval(intervalRef.current);
    }

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [isPlaying]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      clearInterval(intervalRef.current);
    };
  }, []);

  if (!visible) return null;
  console.log("callDetails in EmployeeCallDetailSummary:", callDetails);
  const gotoLink =
    typeof callDetails?.extraData === "object"
      ? callDetails?.extraData?.GOTO_CALL_URL
      : JSON.parse(callDetails?.extraData || "{}")?.GOTO_CALL_URL;
  return (
    <Drawer
      title={
        <div className="flex items-center gap-2">
          <span>{callerName ? `Call recording - ${callerName}` : "Call recording summary"}</span>
          {gotoLink && (
            <Tooltip title="Open in GoTo">
              <span
                style={{ display: "inline-flex" }}
                onClick={e => {
                  e.stopPropagation();

                  console.log("GoTo Link:", gotoLink);
                  if (gotoLink) {
                    window.open(gotoLink, "_blank");
                  }
                }}
              >
                <LinkIcon className="link-icon m-2 cursor-pointer text-[#1677FF]" />
              </span>
            </Tooltip>
          )}
        </div>
      }
      placement="right"
      width={window.innerWidth <= 768 ? "100%" : "40%"}
      onClose={onClose}
      open={visible}
      bodyStyle={{ padding: 0, background: "#f7f9fb" }}
      closable
      headerStyle={{ borderBottom: "1px solid #f0f0f0", background: "#fff" }}
    >
      <div className="p-6">
        {/* Audio Player Section */}
        <div className="bg-white rounded-lg p-5 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-4 w-full">
            <CallAudioPlayer
              visible
              title={callDetails.toName ? `Call with ${callDetails.toName}` : "Call Audio"}
              audioSrc={audioSrc}
              duration={duration}
              timestamp={callDetails.startedAt || ""}
              onClose={e => {
                e.stopPropagation();
              }}
              hideHeader
              hideExtra
              hideTimestamp
              bordered={false}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div>
              <span className="text-gray-400 text-sm">Date/Time</span>
              <div className="text-sm text-gray-700">
                {convertUtcToLocal(callDetails?.startedAt, DATETIME_FORMAT.US_DATETIME)}
              </div>
            </div>
            <div>
              <span className="text-gray-400 text-sm">Duration</span>
              <div className="text-sm text-gray-700">{duration || "0m 00s"}</div>
            </div>
            <div>
              <span className="text-gray-400 text-sm">Call Type</span>
              <div className="text-sm text-gray-700">
                {TitleCase(callDetails?.direction)}{" "}
                {callDetails?.direction === "OUTBOUND" &&
                callDetails?.callInitiationRequest?.callInitiationType === "CLICK_TO_CALL" ? (
                  <ExtensionCallIcon />
                ) : null}
              </div>
            </div>
            {/* <div>
              <span className="text-gray-400 text-sm">Location</span>
              <div className="text-sm text-gray-700">{location}</div>
            </div> */}
            <div>
              <span className="text-gray-400 text-sm">Department</span>
              <div className="text-sm text-gray-700">
                {TitleCase(callDetails?.departmentType) || ""}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs for Summary/Transcription */}
        <div className="mb-6">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "summary",
                label: <span className="font-medium">Summary</span>,
                children: (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 mb-4 bg-white shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <AiSentimentIcon className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-gray-700">Otto AI Sentiment</span>
                    </div>
                    <div className="text-sm text-gray-700 p-2">
                      {TitleCase(callDetails?.aiSentiment)}
                    </div>
                    <div className="flex items-center gap-2 mb-2 mt-4">
                      <AiSummaryIcon className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-gray-700">Otto AI Summary</span>
                    </div>
                    <div className="text-sm text-gray-700 p-2">{callDetails?.aiSummary}</div>
                    <div className="mt-4 p-2">
                      <OttoAiCallTopics
                        topics={
                          isString(callDetails?.aiTopics)
                            ? JSON.parse(callDetails?.aiTopics)
                            : callDetails?.aiTopics
                        }
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: "transcription",
                label: <span className="font-medium">Transcription</span>,
                children: (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 mb-4 bg-white shadow-sm">
                    <TranscriptConversation
                      transcripts={transcripts}
                      direction={callDetails?.direction}
                    />
                  </div>
                ),
              },
            ]}
          />
        </div>

        {/* Participants */}
        {activeTab === "summary" && (
          <div className="p-2">
            <div className="font-semibold text-gray-700 mb-2">Participants</div>
            {loading && <LoadingSkeleton />}
            {!loading &&
              !isEmpty(participants) &&
              (() => {
                const { lead, user, remaining } = determineLeadAndUser(
                  participants,
                  callDetails?.direction
                );
                const remainingCount = remaining.length;

                return (
                  <>
                    <div className="flex-wrap gap-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2">
                      {/* Lead participant */}
                      {lead &&
                        (() => {
                          // Extract display name: Priority lead.firstName+lastName > group.name
                          const leadData = lead.participants.find(
                            p => p.lead?.firstName && p.lead?.lastName
                          );
                          const displayName = leadData
                            ? `${leadData.lead.firstName} ${leadData.lead.lastName}`
                            : lead.name;

                          return (
                            <ParticipantCard
                              key="lead"
                              name={displayName}
                              phoneNumbers={lead.phoneNumbers}
                              roles={lead.roles}
                              isReceiver={lead.isReceiver}
                              isCaller={lead.isCaller}
                            />
                          );
                        })()}

                      {/* User participant */}
                      {user &&
                        (() => {
                          // Extract display name: Priority user.firstName+lastName > group.name
                          const userData = user.participants.find(
                            p => p.user?.firstName && p.user?.lastName
                          );
                          const displayName = userData
                            ? `${userData.user.firstName} ${userData.user.lastName}`
                            : user.name;

                          return (
                            <ParticipantCard
                              key="user"
                              name={displayName}
                              phoneNumbers={user.phoneNumbers}
                              roles={user.roles}
                              isReceiver={user.isReceiver}
                              isCaller={user.isCaller}
                            />
                          );
                        })()}

                      {/* Remaining participants when expanded */}
                      {showAllParticipants &&
                        remaining.map((group, idx) => (
                          <ParticipantCard
                            key={idx}
                            name={group.name}
                            phoneNumbers={group.phoneNumbers}
                            roles={group.roles}
                            isReceiver={group.isReceiver}
                            isCaller={group.isCaller}
                          />
                        ))}
                    </div>

                    {/* Expand/Collapse button - outside grid, right aligned */}
                    {remainingCount > 0 && (
                      <ExpandCollapseButton
                        isExpanded={showAllParticipants}
                        count={remainingCount}
                        onToggle={() => setShowAllParticipants(!showAllParticipants)}
                      />
                    )}
                  </>
                );
              })()}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default EmployeeCallDetailSummary;
